import logger from '../lib/logger';
import { supabase, AuthUserWithProfile } from '../lib/supabase';
import sessionService from './sessionService';
import { validateDemoLogin, isDemoUser, DEMO_CONFIG } from '../demo/demoUsers';

// 🚨 ALDRIG använda service_role i frontend - endast anon key
// Denna fil använder ENDAST supabase anon client

// === DEMO ORGANIZATION (DEV ONLY) ===
export const DEMO_ORGANIZATION = {
  id: 'demo-org-001',
  name: 'CaseFlow Demo Organization',
  slug: 'caseflow-demo',
  plan: 'enterprise',
  created_at: new Date().toISOString(),
};

// === DEMO SESSION STORAGE (DEV ONLY) ===
const DEMO_SESSION_KEY = 'caseflow_demo_session';

interface DemoSession {
  version: number;
  user: AuthUserWithProfile;
  expiresAt: number; // timestamp in ms
}

const DEMO_SESSION_VERSION = 2;

const saveDemoSession = (user: AuthUserWithProfile): void => {
  if (!import.meta.env.DEV) return; // Only in DEV mode
  
  const session: DemoSession = {
    user,
    expiresAt: Date.now() + 8 * 60 * 60 * 1000, // 8 hours
  };
  
  try {
    // Store only non-sensitive data (no passwords or tokens)
    const safeSession = {
      version: DEMO_SESSION_VERSION,
      user: {
        id: user.id,
        email: user.email,
        profile: user.profile,
        organization: user.organization,
        email_verified: user.email_verified,
      },
      expiresAt: session.expiresAt,
    };
    localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(safeSession));
    logger.audit('DEMO_SESSION_SAVED', { userId: user.id.substring(0, 8) + '...' });
  } catch (error) {
    logger.error('Kunde inte spara demo-session', { error: String(error) });
  }
};

const getDemoSession = (): DemoSession | null => {
  if (!import.meta.env.DEV) return null; // Only in DEV mode
  
  try {
    const stored = localStorage.getItem(DEMO_SESSION_KEY);
    if (!stored) return null;
    
    const session: DemoSession = JSON.parse(stored);
    
    // Check version - invalidate old sessions without version or with old version
    if (!session.version || session.version < DEMO_SESSION_VERSION) {
      localStorage.removeItem(DEMO_SESSION_KEY);
      logger.audit('DEMO_SESSION_INVALID_VERSION', { version: session.version });
      return null;
    }
    
    // Check if session is expired
    if (Date.now() > session.expiresAt) {
      localStorage.removeItem(DEMO_SESSION_KEY);
      logger.audit('DEMO_SESSION_EXPIRED', {});
      return null;
    }
    
    return session;
  } catch (error) {
    logger.error('Kunde inte läsa demo-session', { error: String(error) });
    localStorage.removeItem(DEMO_SESSION_KEY);
    return null;
  }
};

const clearDemoSession = (): void => {
  if (!import.meta.env.DEV) return; // Only in DEV mode
  
  try {
    localStorage.removeItem(DEMO_SESSION_KEY);
    logger.audit('DEMO_SESSION_CLEARED', {});
  } catch (error) {
    logger.error('Kunde inte rensa demo-session', { error: String(error) });
  }
};

// === USERNAME-TO-EMAIL MAPPING FOR DEMO USERS ===
// Allow users to login with username instead of full email
const usernameToEmail = {
  'Test': 'test@caseflow.demo',
  'Admin': 'admin@caseflow.demo',
  'Agent': 'agent@caseflow.demo',
  'Technician': 'technician@caseflow.demo',
  'Viewer': 'viewer@caseflow.demo',
};

// Normalize login input: trim and handle case-insensitive username matching
// For DEV bypass, also normalize "test" (any case) to "test@caseflow.demo"
const normalizeLoginInput = (emailOrUsername: string): string => {
  const normalizedLogin = emailOrUsername.trim();
  
  // For DEV bypass: if user types "test" (any case), map to test@caseflow.demo
  if (normalizedLogin.toLowerCase() === 'test') {
    return 'test@caseflow.demo';
  }
  
  // If it's already an email (contains @), return as-is
  if (normalizedLogin.includes('@')) return normalizedLogin;
  
  // Check if it's a known username (case-insensitive)
  const lowerInput = normalizedLogin.toLowerCase();
  const matchedKey = Object.keys(usernameToEmail).find(
    key => key.toLowerCase() === lowerInput
  );
  
  if (matchedKey) {
    const mapped = usernameToEmail[matchedKey];
    logger.audit('USERNAME_MAPPED', { username: normalizedLogin, email: mapped.replace(/(.{2}).*@/, '$1***@') });
    return mapped;
  }
  
  return normalizedLogin;
};

export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  role: 'admin' | 'technician' | 'agent' | 'viewer';
  created_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  plan?: string;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  user?: AuthUserWithProfile;
}

// Generic error messages (ingen enumeration)
const LOGIN_ERRORS = {
  INVALID_CREDENTIALS: 'Felaktig email eller lösenord',
  EMAIL_NOT_CONFIRMED: 'Email-adressen har inte bekräftats',
  TOO_MANY_REQUESTS: 'För många inloggningsförsök. Försök igen senare.',
  UNKNOWN_ERROR: 'Ett oväntat fel inträffade. Försök igen.',
};

/**
 * Skapa en mock-användare för demo-inloggning
 * Genererar ett konsekvent UUID baserat på email
 */
// Cache for demo users to ensure stable references (prevents re-render loops)
const demoUserCache = new Map<string, AuthUserWithProfile>();

const createDemoUser = (email: string, role: string, name: string): AuthUserWithProfile => {
  // Return cached version if exists (stable reference)
  const cacheKey = `${email}-${role}`;
  const cached = demoUserCache.get(cacheKey);
  if (cached) return cached;
  
  // Skapa en enkel hash för att generera ett mock UUID (utan Date.now för stabilitet)
  const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const mockId = `demo-${hash}-${role}`;
  
  const user: AuthUserWithProfile = {
    id: mockId,
    email: email,
    organization_id: DEMO_ORGANIZATION.id,
    profile: {
      full_name: name,
      role: role as 'admin' | 'technician' | 'agent' | 'viewer',
    },
    organization: { ...DEMO_ORGANIZATION },
    email_verified: true,
  };
  
  // Cache the user object for stable references
  demoUserCache.set(cacheKey, user);
  return user;
};

/**
 * Logga in med email och lösenord
 * @param email - Användarens email eller användarnamn
 * @param password - Användarens lösenord
 * @returns AuthResult med success/error
 */
export const signInWithEmail = async (
  email: string,
  password: string
): Promise<AuthResult> => {
  try {
    // Normalize login input BEFORE any checks
    // Handles: trim, case-insensitive "Test" -> "test@caseflow.demo", username mapping
    const normalizedEmail = normalizeLoginInput(email);

    // Validera input
    if (!normalizedEmail || !password) {
      return { success: false, error: 'Email och lösenord krävs' };
    }

    // Enkel email-validering (inte för strikt för att undvika läckage)
    // För demo-användare, tillåt inloggning utan @-check
    const isDemoLogin = DEMO_CONFIG.enabled && isDemoUser(normalizedEmail);
    if (!normalizedEmail.includes('@') && !isDemoLogin) {
      return { success: false, error: 'Ogiltig email-adress' };
    }

    logger.audit('LOGIN_ATTEMPT', { email: normalizedEmail.replace(/(.{2}).*@/, '$1***@') }); // Maskera email i log

    // === LOCAL DEV TEST ACCOUNT BYPASS ===
    // Endast för lokal utveckling - Test/Test konto
    // Detta får ALDRIG köras i produktion
    // Accepts: username "Test" (any case) or email "test@caseflow.demo" with password "Test"
    if (import.meta.env.DEV && normalizedEmail === 'test@caseflow.demo' && password === 'Test') {
      logger.audit('LOCAL_DEV_LOGIN', { email: 'te***@caseflow.demo', role: 'admin' });
      
      const mockUser = createDemoUser('test@caseflow.demo', 'admin', 'Test User');
      
      // Save demo session for persistence
      saveDemoSession(mockUser);
      
      // Initiera session-timeout för demo-läge
      sessionService.initializeSession({
        onIdleWarning: () => {
          console.warn('Sessionsvarning: Du loggas ut snart på grund av inaktivitet.');
        },
        onSessionLogout: () => {
          signOut();
          window.location.href = '/login?reason=idle';
        },
      });
      
      return { success: true, user: mockUser };
    }

    // === DEMO LOGIN CHECK ===
    // Kontrollera om det är en demo-användare och vi är i demo-läge
    if (DEMO_CONFIG.enabled && isDemoUser(normalizedEmail)) {
      logger.audit('DEMO_LOGIN_ATTEMPT', { email: normalizedEmail.replace(/(.{2}).*@/, '$1***@') });
      
      const demoUser = validateDemoLogin(normalizedEmail, password);
      
      if (demoUser) {
        logger.audit('DEMO_LOGIN_SUCCESS', { email: email.replace(/(.{2}).*@/, '$1***@'), role: demoUser.role });
        
        // Skapa mock-användare för demo
        const mockUser = createDemoUser(demoUser.email, demoUser.role, demoUser.name);
        
        // Save demo session for persistence
        saveDemoSession(mockUser);
        
        // Initiera session-timeout för demo-läge
        sessionService.initializeSession({
          onIdleWarning: () => {
            console.warn('Sessionsvarning: Du loggas ut snart på grund av inaktivitet.');
          },
          onSessionLogout: () => {
            signOut();
            window.location.href = '/login?reason=idle';
          },
        });
        
        return { success: true, user: mockUser };
      } else {
        logger.audit('DEMO_LOGIN_FAILED', { email: email.replace(/(.{2}).*@/, '$1***@') });
        return { success: false, error: LOGIN_ERRORS.INVALID_CREDENTIALS };
      }
    }

    // === NORMAL SUPABASE AUTH ===
    // Check if supabase is configured
    if (!supabase) {
      logger.error('Försök till inloggning men Supabase är inte konfigurerat', {});
      return { success: false, error: 'Inloggning är inte tillgänglig just nu. Försök igen senare.' };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (error) {
      logger.error('LOGIN_FAILED', { error: error.message, email: '***' });
      
      // Mappa Supabase felmeddelanden till användarvänliga meddelanden (generic - ingen enumeration)
      // VIKTIGT: Använd inte Supabase's exakta felmeddelanden i koden för att undvika enumeration
      const errorMsg = error.message.toLowerCase();
      
      // Check for email not confirmed (utan att använda exakta strängen)
      if (errorMsg.includes('email') && errorMsg.includes('confirm')) {
        return { success: false, error: LOGIN_ERRORS.EMAIL_NOT_CONFIRMED };
      }
      
      // Check for too many requests
      if (errorMsg.includes('too') && errorMsg.includes('request')) {
        return { success: false, error: LOGIN_ERRORS.TOO_MANY_REQUESTS };
      }
      
      // Generic error för alla andra fel (inklusive invalid credentials)
      return { success: false, error: LOGIN_ERRORS.INVALID_CREDENTIALS };
    }

    if (!data.user || !data.session) {
      return { success: false, error: 'Ingen användare hittades' };
    }

    // Kontroll: Email verification
    const isEmailVerified = data.user.email_confirmed_at != null;
    if (!isEmailVerified) {
      logger.audit('LOGIN_FAILED - email not verified', { userId: data.user.id });
      return { success: false, error: 'Email-adressen har inte bekräftats. Kontrollera din inkorg.' };
    }

    logger.audit('LOGIN_SUCCESS', { userId: data.user.id, email: '***' });

    // Initiera session-timeout efter lyckad inloggning
    sessionService.initializeSession({
      onIdleWarning: () => {
        console.warn('Sessionsvarning: Du loggas ut snart på grund av inaktivitet.');
      },
      onSessionLogout: () => {
        // Rensa auth state vid idle timeout
        signOut();
        window.location.href = '/login?reason=idle';
      },
    });

    // Hämta profil och organisation
    const userWithProfile: AuthUserWithProfile = {
      id: data.user.id,
      email: data.user.email || normalizedEmail,
    };

    // Försök hämta profil (men blockera inte inloggning om det misslyckas)
    try {
      const profile = await getUserProfile(data.user.id);
      if (profile) {
        userWithProfile.profile = {
          full_name: profile.full_name,
          role: profile.role,
        };
      }
    } catch (profileError) {
      logger.warn('Kunde inte hämta profil vid inloggning', { error: String(profileError) });
    }

    // Försök hämta organisation (men blockera inte inloggning)
    try {
      const organization = await getUserOrganization(data.user.id);
      if (organization) {
        userWithProfile.organization = {
          id: organization.id,
          name: organization.name,
          slug: organization.slug,
        };
      }
    } catch (orgError) {
      logger.warn('Kunde inte hämta organisation vid inloggning', { error: String(orgError) });
    }

    return { success: true, user: userWithProfile };
  } catch (error) {
    logger.error('Oväntat fel vid inloggning', { error: String(error) });
    return { success: false, error: LOGIN_ERRORS.UNKNOWN_ERROR };
  }
};

/**
 * Logga ut användaren
 * Rensar session och känslig data
 */
export const signOut = async (): Promise<void> => {
  try {
    logger.audit('LOGOUT', {});
    
    // Clear demo session (DEV ONLY)
    clearDemoSession();
    
    // Använd sessionService för säker utloggning
    sessionService.secureLogout();
    
    // Supabase signOut (only if configured)
    if (supabase) {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logger.error('Fel vid utloggning från Supabase', { error: error.message });
      }
    } else {
      logger.info('Supabase inte konfigurerat, hoppar över Supabase signOut', {});
    }

    // Rensa sessionStorage (där auth-token lagras)
    sessionStorage.removeItem('caseflow-auth');
    
    // Rensa eventuell känslig data från localStorage (förutom theme/locale)
    const allowedKeys = ['theme', 'locale', 'tenant-id'];
    Object.keys(localStorage).forEach(key => {
      if (!allowedKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Städa session timers
    sessionService.cleanupSession();

    logger.audit('LOGOUT_COMPLETED', {});
  } catch (error) {
    logger.error('Oväntat fel vid utloggning', { error: String(error) });
    // Tvinga rensning även vid fel
    sessionStorage.clear();
  }
};

/**
 * Hämta aktuell session
 * @returns session eller null
 */
export const getCurrentSession = async () => {
  try {
    // Check for demo session first (DEV ONLY)
    if (import.meta.env.DEV) {
      const demoSession = getDemoSession();
      if (demoSession) {
        logger.audit('DEMO_SESSION_RESTORED', { userId: demoSession.user.id.substring(0, 8) + '...' });
        // Return a mock session object that ProtectedRoute will accept
        // Include organization to ensure OrganizationAccessGate works
        return {
          user: {
            id: demoSession.user.id,
            email: demoSession.user.email,
            organization: demoSession.user.organization,
            email_verified: demoSession.user.email_verified || true,
          },
          expires_at: Math.floor(demoSession.expiresAt / 1000),
        };
      }
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte hämta session', {});
      return null;
    }

    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      logger.error('Kunde inte hämta session', { error: error.message });
      return null;
    }

    return session;
  } catch (error) {
    logger.error('Oväntat fel vid hämtning av session', { error: String(error) });
    return null;
  }
};

/**
 * Hämta aktuell användare
 * @returns user eller null
 */
export const getCurrentUser = async () => {
  try {
    // Check for demo session first (DEV ONLY)
    if (import.meta.env.DEV) {
      const demoSession = getDemoSession();
      if (demoSession) {
        logger.audit('DEMO_USER_RESTORED', { userId: demoSession.user.id.substring(0, 8) + '...' });
        // Return a mock user object with organization
        return {
          id: demoSession.user.id,
          email: demoSession.user.email,
          organization: demoSession.user.organization,
          email_verified: demoSession.user.email_verified || true,
        };
      }
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte hämta användare', {});
      return null;
    }

    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) {
      logger.error('Kunde inte hämta användare', { error: error.message });
      return null;
    }

    return user;
  } catch (error) {
    logger.error('Oväntat fel vid hämtning av användare', { error: String(error) });
    return null;
  }
};

/**
 * Hämta användarens profil från databasen
 * @param userId - Användarens UUID
 * @returns UserProfile eller null
 */
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    if (!userId) {
      logger.warn('getUserProfile anropad utan userId', {});
      return null;
    }

    // Om det är en demo-användare, returnera mock-profil
    if (userId.startsWith('demo-')) {
      // Extrahera roll från demo-ID:t
      const roleMatch = userId.match(/demo-\d+-(.+?)-\w+/);
      const role = roleMatch ? roleMatch[1] as 'admin' | 'technician' | 'agent' | 'viewer' : 'viewer';
      
      return {
        id: userId,
        email: 'demo@caseflow.demo',
        full_name: 'Demo User',
        role: role,
        created_at: new Date().toISOString(),
      };
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte hämta profil', { userId: userId.substring(0, 8) + '...' });
      return null;
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, role, created_at')
      .eq('id', userId)
      .single();

    if (error) {
      // Om profil inte finns, returnera null (inte kritisk fel)
      if (error.code === 'PGRST116') {
        logger.info('Ingen profil hittad för användare', { userId: userId.substring(0, 8) + '...' });
        return null;
      }
      logger.error('Kunde inte hämta profil', { error: error.message, userId: userId.substring(0, 8) + '...' });
      return null;
    }

    return data as UserProfile;
  } catch (error) {
    logger.error('Oväntat fel vid hämtning av profil', { error: String(error) });
    return null;
  }
};

/**
 * Hämta användarens organisation
 * @param userId - Användarens UUID
 * @returns Organization eller null
 */
export const getUserOrganization = async (userId: string): Promise<Organization | null> => {
  try {
    if (!userId) {
      logger.warn('getUserOrganization anropad utan userId', {});
      return null;
    }

    // Om det är en demo-användare, returnera mock-organisation
    if (userId.startsWith('demo-')) {
      return { ...DEMO_ORGANIZATION };
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte hämta organisation', { userId: userId.substring(0, 8) + '...' });
      return null;
    }

    // Hämta organisation via organization_members koppling
    const { data, error } = await supabase
      .from('organization_members')
      .select(`
        organization_id,
        organizations:organization_id (
          id,
          name,
          slug,
          created_at
        )
      `)
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        logger.info('Användare tillhör ingen organisation', { userId: userId.substring(0, 8) + '...' });
        return null;
      }
      logger.error('Kunde inte hämta organisation', { error: error.message, userId: userId.substring(0, 8) + '...' });
      return null;
    }

    if (!data?.organizations) {
      return null;
    }

    // Hantera både array och objekt (beroende på Supabase version)
    const org = Array.isArray(data.organizations) ? data.organizations[0] : data.organizations;
    
    return org as unknown as Organization;
  } catch (error) {
    logger.error('Oväntat fel vid hämtning av organisation', { error: String(error) });
    return null;
  }
};

/**
 * Hämta användarens roll
 * @param userId - Användarens UUID
 * @returns roll eller 'viewer' som default
 */
export const getUserRole = async (userId: string): Promise<string> => {
  try {
    const profile = await getUserProfile(userId);
    return profile?.role || 'viewer';
  } catch (error) {
    logger.error('Kunde inte hämta användarroll', { error: String(error) });
    return 'viewer'; // Säker default
  }
};

/**
 * Kontrollera om användarens email är verifierad
 * @param userId - Användarens UUID
 * @returns boolean
 */
export const isEmailVerified = async (userId: string): Promise<boolean> => {
  try {
    // För demo-användare, alltid verifierad
    if (userId.startsWith('demo-')) {
      return true;
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte kontrollera email verification', { userId: userId.substring(0, 8) + '...' });
      return false;
    }

    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      return false;
    }

    return user.email_confirmed_at != null;
  } catch (error) {
    logger.error('Kunde inte kontrollera email verification', { error: String(error) });
    return false;
  }
};

/**
 * Uppdatera session (refresh)
 */
export const refreshSession = async () => {
  try {
    logger.audit('SESSION_REFRESH_ATTEMPT', {});
    
    // Check for demo session first (DEV ONLY)
    if (import.meta.env.DEV) {
      const demoSession = getDemoSession();
      if (demoSession) {
        logger.audit('SESSION_REFRESH_USING_DEMO_SESSION', {});
        return {
          user: {
            id: demoSession.user.id,
            email: demoSession.user.email,
            organization: demoSession.user.organization,
            email_verified: demoSession.user.email_verified || true,
          },
          expires_at: Math.floor(demoSession.expiresAt / 1000),
        };
      }
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte uppdatera session', {});
      return null;
    }

    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      logger.error('SESSION_EXPIRED', { error: error.message });
      return null;
    }

    logger.audit('SESSION_REFRESH_SUCCESS', {});
    return data.session;
  } catch (error) {
    logger.error('Oväntat fel vid session refresh', { error: String(error) });
    return null;
  }
};

/**
 * Lyssna på auth-state ändringar
 * @param callback - Anropas vid auth-state ändring
 * @returns Unsubscribe funktion
 */
export const onAuthStateChange = (callback: (event: string, session: unknown) => void) => {
  // Return a mock subscription if supabase is not configured
  if (!supabase) {
    logger.info('Supabase inte konfigurerat, ingen auth state listener', {});
    return {
      data: {
        subscription: {
          unsubscribe: () => {},
        },
      },
    };
  }

  return supabase.auth.onAuthStateChange((event, session) => {
    logger.audit('AUTH_STATE_CHANGE', { event, hasSession: !!session });
    
    // If no Supabase session but we have a demo session, use demo session (DEV ONLY)
    if (!session && import.meta.env.DEV) {
      const demoSession = getDemoSession();
      if (demoSession) {
        logger.audit('AUTH_STATE_CHANGE_USING_DEMO_SESSION', { event });
        const mockSession = {
          user: {
            id: demoSession.user.id,
            email: demoSession.user.email,
            organization: demoSession.user.organization,
            email_verified: demoSession.user.email_verified || true,
          },
          expires_at: Math.floor(demoSession.expiresAt / 1000),
        };
        callback(event, mockSession);
        return;
      }
    }
    
    callback(event, session);
  });
};
